import { useEditConsumer } from "@features/cx/consumerManagement/hooks";
import {
  useOwnerType,
  usePlans,
  usePlanDetails,
  useCreatePlanPayment,
  useGetPlanParameters,
  useUpdateConsumer,
  useRegistrationCharges,
  useTerritoryPremiseStats,
  useUpdateConsumerUnitPlan,
} from "@features/cx/consumerOnboarding/hooks";
import { usePatchConfigMap } from "@features/onboarding/utility-setup/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Button } from "@shared/ui/button";
import { FileText, Search, Briefcase, AlertTriangle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PaymentDeferredDialog from "./payLaterModal";
import PaymentReceipt from "./paymentCompletedCard";
import PaymentDialog, { PaymentData } from "./paymentModal";
import RateDetailsDialog from "./rateModal";
import SelectedPlanCard from "./selectedPlanCard";
import { queryClient } from "@shared/api/queries/queryClient";
import { useMasterConsumerSearch } from "@features/cx/consumer-onboarding/hooks";
import { isPremiseButtonClicked } from "./selectPremise";
import { formatTerritoryAddress } from "@shared/utils/address";

// TypeScript Interfaces
export interface PlanUtilityService {
  name: string;
  type: string;
}

export interface ServicePlan {
  id: string;
  name: string;
  description: string;
  billingFrequency: string;
  processingFees: number;
  connectionFee: number;
  securityDeposit: number;
  utilityServices: PlanUtilityService[];
}

export interface SetAccountAndPlanProps {
  servicePlans?: ServicePlan[];
  onPlanSelect?: (planId: string, plan: ServicePlan) => void;
  onPaymentComplete?: (data: PaymentData) => void | Promise<void>;
  onPayLater?: (planId: string) => void;
  initialTags?: string[];
  initialPlanId?: string;
  className?: string;
  paymentModes?: Array<{ value: string; label: string }>;
  paymentGateways?: Array<{ value: string; label: string }>;
  customPaymentModes?: Array<{ value: string; label: string }>;
  currentStepIndex?: number;
  consumerDetailsData?: any;
  selectedUnitCode?: string;
  isReadOnly?: boolean;
}
export const SetAccountAndPlan: React.FC<SetAccountAndPlanProps> = ({
  servicePlans: propServicePlans,
  onPlanSelect,
  onPaymentComplete,
  onPayLater,
  initialTags = [],
  initialPlanId,
  className = "",
  paymentModes,
  paymentGateways,
  currentStepIndex = 2,
  consumerDetailsData,
  selectedUnitCode: propSelectedUnitCode,
  isReadOnly = false,
}) => {
  const rawUnitCode =
    propSelectedUnitCode ||
    consumerDetailsData?.result?.unitInfo?.basicInfo?.code ||
    consumerDetailsData?.result?.unitInfo?.code ||
    consumerDetailsData?.result?.unitCode ||
    "";

  const selectedUnitCode =
    rawUnitCode && (rawUnitCode.includes("#") || rawUnitCode.includes("_"))
      ? rawUnitCode
      : "";

  const { data: territoryPremiseStatsData } =
    useTerritoryPremiseStats(selectedUnitCode);

  const unitPlanId =
    territoryPremiseStatsData?.basicInfo?.planId ||
    territoryPremiseStatsData?.planInfo?.id ||
    consumerDetailsData?.result?.unitInfo?.basicInfo?.planId;

  const unitPlanName =
    territoryPremiseStatsData?.planInfo?.planName ||
    territoryPremiseStatsData?.planInfo?.name ||
    consumerDetailsData?.result?.unitInfo?.planInfo?.planName ||
    consumerDetailsData?.result?.unitInfo?.planInfo?.name ||
    consumerDetailsData?.result?.basicInfo?.planName ||
    consumerDetailsData?.result?.planDetails?.planName ||
    "";

  const hasUnitPlan = Boolean(unitPlanId || unitPlanName);
  // Hooks - IMPORTANT: Call hooks at the top level
  const consumerUtilityServices = consumerDetailsData?.result?.utilityService;
  const { data: planData } = usePlans(consumerUtilityServices);
  const currency = useCurrency();
  const navigate = useNavigate();
  const editConsumerMutation = useEditConsumer();
  const { consumerId } = useParams<{ consumerId: string }>();
  const nextStep = `/cx/consumer-onboarding/workflow/installation/${consumerId}/INSTALLATION INPROGRESS`;
  const remoteUtilityId = getRemoteUtilityId();
  const updateConsumerUnitPlanMutation = useUpdateConsumerUnitPlan();
  const patchConfigMapMutation = usePatchConfigMap();
  const createPlanPaymentMutation = useCreatePlanPayment();
  const { data: ownerTypeData } = useOwnerType();
  const ownerTypeOptions = useMemo(
    () =>
      ownerTypeData?.result?.map((ownerType: any) => ({
        value: ownerType?.key?.toString(),
        label: ownerType.value,
      })) || [],
    [ownerTypeData],
  );
  // State
  const [selectedPlanId, setSelectedPlanId] = useState<string>(
    initialPlanId || "",
  );
  const [selectingPlanId, setSelectingPlanId] = useState<string>(""); // Track which plan is being selected
  const [searchQuery, setSearchQuery] = useState("");
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState("");
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isDeferredDialogOpen, setIsDeferredDialogOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [completedPaymentData, setCompletedPaymentData] =
    useState<PaymentData | null>(null);
  const [isRateDetailsOpen, setIsRateDetailsOpen] = useState(false);
  const [accountType, setAccountType] = useState<string>("");
  const [masterSearchQuery, setMasterSearchQuery] = useState("");
  const [selectedMasterAccount, setSelectedMasterAccount] = useState<any>(null);
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerTypeField, setOwnerTypeField] = useState("");
  const [ownerAddress, setOwnerAddress] = useState("");
  const [isBillingAccount, setIsBillingAccount] = useState<string>("yes");
  const [isNoPlanSelected, setIsNoPlanSelected] = useState<boolean>(false);
  const [parameterValues, setParameterValues] = useState<
    Record<string, string>
  >({});
  const [isPayLaterWarningOpen, setIsPayLaterWarningOpen] = useState(false);
  const updateConsumerMutation = useUpdateConsumer();
  const updateConsumer = updateConsumerMutation.mutate;

  const { data: planDetailsData, isLoading: isLoadingPlanDetails } =
    usePlanDetails(
      isRateDetailsOpen ? selectedPlanId : "",
      isRateDetailsOpen ? remoteUtilityId : "",
    );

  const selectedPlanRaw = planData?.result?.find(
    (p: any) => p?.id?.toString() === selectedPlanId,
  );

  const selectedPlanUtilityServices = useMemo(() => {
    let services =
      selectedPlanRaw?.utilityService || consumerUtilityServices || [];
    if (Array.isArray(services)) {
      return services
        .map((s: any) =>
          typeof s === "string" ? s : s?.name || s?.type || s?.id || String(s),
        )
        .filter(Boolean);
    }
    return [];
  }, [selectedPlanRaw, consumerUtilityServices]);

  const { data: planParametersData } = useGetPlanParameters(
    selectedPlanId,
    remoteUtilityId,
    selectedPlanUtilityServices,
  );
  const { data: registrationChargesData } = useRegistrationCharges(
    selectedPlanId,
    remoteUtilityId,
  );
  const { data: masterAccountsObj, isLoading: isSearchingMaster } =
    useMasterConsumerSearch({
      remoteUtilityId: remoteUtilityId.toString(),
      searchData: masterSearchQuery,
      requestType: "",
    });

  const masterAccounts = useMemo(() => {
    return masterAccountsObj?.result || [];
  }, [masterAccountsObj]);
  const serviceAddress =
    formatTerritoryAddress(selectedMasterAccount?.territoryData, "service") ||
    "No address configured";

  // Auto-populate account type and details from consumerDetailsData
  useEffect(() => {
    if (consumerDetailsData?.result) {
      const data = consumerDetailsData?.result;
      const ownershipDetails = data?.ownershipDetails || {};
      const structureChoice = ownershipDetails?.structureChoice;

      let type = "";
      if (structureChoice === "Master") {
        type = "0";
      } else if (structureChoice === "Sub_account") {
        type = "1";
        if (ownershipDetails?.masterAccountNo) {
          setSelectedMasterAccount({
            accountNumber: ownershipDetails?.masterAccountNo,
            accountName:
              ownershipDetails?.masterAccountName ||
              ownershipDetails?.masterAccountEmail ||
              "N/A",
            subAccountCount: ownershipDetails?.subAccountCount,
            serviceAddress: "N/A",
            territoryData:
              ownershipDetails?.masterAccountTerritoryData ||
              ownershipDetails?.territoryData,
          });
        }
      } else if (
        structureChoice === "INDIVIDUAL" ||
        structureChoice === "Individual"
      ) {
        type = "2";
      } else if (structureChoice === "Tenant") {
        type = "4";
        const ownerDetail = ownershipDetails?.ownershipDetail || {};
        setOwnerName(ownerDetail?.ownerName || "");
        setOwnerPhone(ownerDetail?.ownerPhone || "");
        setOwnerEmail(ownerDetail?.ownerEmail || "");
        setOwnerTypeField(ownerDetail?.ownerType || "");
        setOwnerAddress(ownerDetail?.ownerAddress || "");
      }
      setAccountType(type);
    }
  }, [consumerDetailsData]);

  // Auto-populate plan parameters from consumerDetailsData additionalData / planParameters
  useEffect(() => {
    const res = consumerDetailsData?.result;
    const additionalData = res?.additionalData || res?.additional_data;
    const savedParams =
      additionalData?.planParameters || additionalData?.plan_parameters || [];

    if (Array.isArray(savedParams) && savedParams.length > 0) {
      const initialValues: Record<string, string> = {};

      savedParams.forEach((sp: any) => {
        const val =
          sp.value !== undefined && sp.value !== null ? String(sp.value) : "";
        const code = sp.parameterCode || sp.parameter_code;
        const name = sp.parameterName || sp.parameter_name;
        const id = sp.parameterId || sp.parameter_id;

        if (code) initialValues[code] = val;
        if (name) initialValues[name] = val;
        if (id) initialValues[id] = val;
      });

      if (planParametersData?.result) {
        planParametersData.result.forEach((param: any) => {
          const paramId = param.parameterId || param.parameter_id;
          const code =
            param.code || param.parameterCode || param.parameter_code;
          const name =
            param.name || param.parameterName || param.parameter_name;

          const match = savedParams.find(
            (sp: any) =>
              (code &&
                (sp.parameterCode === code || sp.parameter_code === code)) ||
              (name &&
                (sp.parameterName === name || sp.parameter_name === name)) ||
              (paramId &&
                (sp.parameterId === paramId || sp.parameter_id === paramId)),
          );

          if (match && match.value !== undefined && match.value !== null) {
            const val = String(match.value);
            if (paramId) initialValues[paramId] = val;
            if (code) initialValues[code] = val;
            if (name) initialValues[name] = val;
          }
        });
      }

      if (Object.keys(initialValues).length > 0) {
        setParameterValues((prev) => ({ ...initialValues, ...prev }));
      }
    }
  }, [consumerDetailsData, planParametersData]);

  // Derive fallback plan object directly from consumerDetailsData if present
  const consumerPlanObj = useMemo(() => {
    const res = consumerDetailsData?.result;
    const rawPlan =
      res?.planDetails ||
      res?.plan_details ||
      res?.unitInfo?.planInfo ||
      res?.unit_info?.plan_info;

    if (!rawPlan) return null;

    const planId = (
      rawPlan.id ??
      rawPlan.planId ??
      rawPlan.plan_id ??
      res?.remotePlanId ??
      res?.remote_plan_id ??
      ""
    ).toString();

    const planName =
      rawPlan.planName || rawPlan.plan_name || rawPlan.name || "Unnamed Plan";

    const description =
      rawPlan.description || rawPlan.shortName || rawPlan.short_name || "";

    const billingFrequency =
      rawPlan.billingFrequencyDisplay ||
      rawPlan.billing_frequency_display ||
      rawPlan.billingFrequency ||
      rawPlan.billing_frequency ||
      "MONTHLY";

    const servicesList =
      rawPlan.utilityService ||
      rawPlan.utility_service ||
      rawPlan.utilityServices ||
      rawPlan.utility_services ||
      res?.utilityService ||
      res?.utility_service ||
      [];

    const utilityServices = Array.isArray(servicesList)
      ? servicesList.map((svc: any) => ({
          name: typeof svc === "string" ? svc : svc?.name || String(svc),
          type: typeof svc === "string" ? "" : svc?.type || "",
        }))
      : [];

    return {
      id: planId,
      name: planName,
      description,
      billingFrequency: String(billingFrequency),
      processingFees: Number(
        rawPlan.processingFees ?? rawPlan.processing_fees ?? 0,
      ),
      connectionFee: Number(
        rawPlan.connectionFee ?? rawPlan.connection_fee ?? 0,
      ),
      securityDeposit: Number(
        rawPlan.securityDeposit ?? rawPlan.security_deposit ?? 0,
      ),
      utilityServices,
    } as ServicePlan;
  }, [consumerDetailsData]);

  // Derive unit plan object directly from territoryPremiseStatsData (from premise/unit selection API)
  const unitPlanObj = useMemo(() => {
    const rawPlan =
      territoryPremiseStatsData?.plan_info ||
      territoryPremiseStatsData?.planInfo;

    if (!rawPlan) return null;

    const planId = (
      rawPlan.id ??
      rawPlan.plan_id ??
      rawPlan.planId ??
      territoryPremiseStatsData?.basic_info?.plan_id ??
      territoryPremiseStatsData?.basicInfo?.planId ??
      ""
    ).toString();

    const planName =
      rawPlan.plan_name || rawPlan.planName || rawPlan.name || "Unnamed Plan";

    const description =
      rawPlan.description || rawPlan.short_name || rawPlan.shortName || "";

    const billingFrequency =
      rawPlan.billing_frequency_display ||
      rawPlan.billingFrequencyDisplay ||
      rawPlan.billing_frequency ||
      rawPlan.billingFrequency ||
      "MONTHLY";

    const servicesList =
      rawPlan.utility_service ||
      rawPlan.utilityService ||
      rawPlan.utility_services ||
      rawPlan.utilityServices ||
      [];

    const utilityServices = Array.isArray(servicesList)
      ? servicesList.map((svc: any) => ({
          name: typeof svc === "string" ? svc : svc?.name || String(svc),
          type: typeof svc === "string" ? "" : svc?.type || "",
        }))
      : [];

    return {
      id: planId,
      name: planName,
      description,
      billingFrequency: String(billingFrequency),
      processingFees: Number(
        rawPlan.processing_fees ?? rawPlan.processingFees ?? 0,
      ),
      connectionFee: Number(
        rawPlan.connection_fee ?? rawPlan.connectionFee ?? 0,
      ),
      securityDeposit: Number(
        rawPlan.security_deposit ?? rawPlan.securityDeposit ?? 0,
      ),
      utilityServices,
    } as ServicePlan;
  }, [territoryPremiseStatsData]);

  // Transform API response to component format with comprehensive null checks
  const transformedPlans: ServicePlan[] = React.useMemo(() => {
    if (!planData || !planData.result || !Array.isArray(planData.result)) {
      return [];
    }

    return planData.result
      .filter(
        (plan: any) =>
          plan &&
          (plan.id !== undefined ||
            plan.planId !== undefined ||
            plan.plan_id !== undefined ||
            plan.code !== undefined ||
            plan.planName ||
            plan.plan_name ||
            plan.name),
      )
      .map((plan: any) => ({
        id: (
          plan?.id ??
          plan?.planId ??
          plan?.plan_id ??
          plan?.code ??
          ""
        ).toString(),
        name: plan?.planName || plan?.plan_name || plan?.name || "Unnamed Plan",
        description:
          plan?.description || plan?.shortName || plan?.short_name || "",
        billingFrequency:
          plan?.billingFrequencyDisplay ||
          plan?.billing_frequency_display ||
          plan?.billingFrequency ||
          plan?.billing_frequency ||
          "NA",
        processingFees: Number(
          plan?.processingFees ?? plan?.processing_fees ?? 0,
        ),
        connectionFee: Number(plan?.connectionFee ?? plan?.connection_fee ?? 0),
        securityDeposit: Number(
          plan?.securityDeposit ?? plan?.security_deposit ?? 0,
        ),
        utilityServices: Array.isArray(
          plan?.utilityService || plan?.utility_service,
        )
          ? (plan.utilityService || plan.utility_service).map(
              (service: any) => ({
                name:
                  typeof service === "string"
                    ? service
                    : service?.name || String(service),
                type: typeof service === "string" ? "" : service?.type || "",
              }),
            )
          : [],
      }));
  }, [planData]);

  // Use transformed plans
  const servicePlans = transformedPlans;

  // Auto-populate selected plan from consumerDetailsData, territoryPremiseStatsData, or matching plan in servicePlans
  useEffect(() => {
    // Before selecting a unit, do not auto-select or display any plan card
    if (!selectedUnitCode) {
      setSelectedPlanId("");
      setIsPlanConfirmed(false);
      return;
    }

    const res = consumerDetailsData?.result;
    const unitPlan = unitPlanObj || consumerPlanObj;

    const unitPlanId =
      unitPlan?.id ||
      territoryPremiseStatsData?.plan_info?.id ||
      territoryPremiseStatsData?.planInfo?.id ||
      territoryPremiseStatsData?.basic_info?.plan_id ||
      territoryPremiseStatsData?.basicInfo?.planId ||
      res?.unitInfo?.planInfo?.id ||
      res?.unit_info?.plan_info?.id ||
      res?.unitInfo?.basicInfo?.planId ||
      res?.unit_info?.basic_info?.plan_id;

    const unitPlanTitle =
      unitPlan?.name ||
      territoryPremiseStatsData?.plan_info?.plan_name ||
      territoryPremiseStatsData?.planInfo?.planName ||
      res?.unitInfo?.planInfo?.planName ||
      res?.basicInfo?.planName ||
      res?.planDetails?.planName;

    // 1. If unit has an attached plan ID, auto-select it directly
    if (unitPlanId) {
      setSelectedPlanId(unitPlanId.toString());
      setIsPlanConfirmed(true);
      return;
    }

    // 2. If unit has an attached plan title/name, find matching plan in servicePlans
    if (unitPlanTitle && servicePlans.length > 0) {
      const matchingPlan = servicePlans.find(
        (p) =>
          p.name?.trim().toLowerCase() ===
            unitPlanTitle?.trim().toLowerCase() ||
          p.id?.toString() === unitPlanTitle?.toString(),
      );
      if (matchingPlan) {
        setSelectedPlanId(matchingPlan.id);
        setIsPlanConfirmed(true);
        return;
      }
    }

    // 3. Fallback to consumerDetailsData planDetails
    const consumerPlanId =
      res?.planDetails?.id ||
      res?.plan_details?.id ||
      res?.remotePlanId ||
      res?.remote_plan_id;

    if (consumerPlanId) {
      setSelectedPlanId(consumerPlanId.toString());
      return;
    }

    if (consumerPlanObj?.id) {
      setSelectedPlanId(consumerPlanObj.id.toString());
      return;
    }
  }, [
    selectedUnitCode,
    territoryPremiseStatsData,
    unitPlanObj,
    consumerDetailsData,
    servicePlans,
    consumerPlanObj,
  ]);

  const handleAccountTypeChange = (newType: string) => {
    setAccountType(newType);

    const ownershipDetails =
      consumerDetailsData?.result?.ownershipDetails ||
      consumerDetailsData?.result?.ownership_details ||
      {};

    // Restore or clear Tenant details
    if (newType === "4") {
      const ownerDetail =
        ownershipDetails?.ownershipDetail ||
        ownershipDetails?.ownership_detail ||
        {};
      setOwnerName(ownerDetail?.ownerName || ownerDetail?.owner_name || "");
      setOwnerPhone(ownerDetail?.ownerPhone || ownerDetail?.owner_phone || "");
      setOwnerEmail(ownerDetail?.ownerEmail || ownerDetail?.owner_email || "");
      setOwnerTypeField(
        ownerDetail?.ownerType || ownerDetail?.owner_type || "",
      );
      setOwnerAddress(
        ownerDetail?.ownerAddress || ownerDetail?.owner_address || "",
      );
    } else {
      setOwnerName("");
      setOwnerPhone("");
      setOwnerEmail("");
      setOwnerTypeField("");
      setOwnerAddress("");
    }

    // Restore or clear Master Account details
    if (newType === "1") {
      if (
        ownershipDetails?.masterAccountNo ||
        ownershipDetails?.master_account_no
      ) {
        setSelectedMasterAccount({
          accountNumber:
            ownershipDetails?.masterAccountNo ||
            ownershipDetails?.master_account_no,
          accountName:
            ownershipDetails?.masterAccountName ||
            ownershipDetails?.master_account_name ||
            ownershipDetails?.masterAccountEmail ||
            "N/A",
          subAccountCount:
            ownershipDetails?.subAccountCount ||
            ownershipDetails?.sub_account_count,
          serviceAddress: "N/A",
          territoryData:
            ownershipDetails?.masterAccountTerritoryData ||
            ownershipDetails?.master_account_territory_data ||
            ownershipDetails?.territoryData ||
            ownershipDetails?.territory_data,
        });
      } else {
        setSelectedMasterAccount(null);
      }
    } else {
      setSelectedMasterAccount(null);
    }

    setMasterSearchQuery("");

    // For Individual (2) or Master (0), call API immediately
    if (newType === "2" || newType === "0") {
      editConsumerMutation.mutate({
        type: (currentStepIndex + 1).toString(),
        consumerId: consumerId!,
        payload: {
          ownership: parseInt(newType),
        },
      });
    }
  };

  const handleSaveTenantInfo = () => {
    if (!ownerName) {
      toast({ title: "Owner Name is required", variant: "destructive" });
      return;
    }
    if (!ownerPhone) {
      toast({
        title: "Owner Phone Number is required",
        variant: "destructive",
      });
      return;
    }
    if (!ownerEmail) {
      toast({ title: "Owner Email is required", variant: "destructive" });
      return;
    }
    editConsumerMutation.mutate({
      type: (currentStepIndex + 1).toString(),
      consumerId: consumerId!,
      payload: {
        ownership: 4,
        approval_details: {
          owner_name: ownerName,
          owner_phone: ownerPhone,
          owner_email: ownerEmail || null,
          owner_type: ownerTypeField,
          owner_address: ownerAddress,
        },
      },
    });
  };

  const handleSelectMasterAccount = (acc: any) => {
    const fullName = `${acc.firstName || ""} ${acc.lastName || ""}`.trim();
    setSelectedMasterAccount({
      accountNumber: acc.consumerNo,
      accountName: fullName,
      subAccountCount: acc.subAccountCount || 0,
      serviceAddress: acc.serviceAddress || "N/A",
      territoryData: acc.territory_data || acc.territoryData,
    });

    editConsumerMutation.mutate({
      type: (currentStepIndex + 1).toString(),
      consumerId: consumerId!,
      payload: {
        ownership: 1,
        approval_details: {
          master_account_id: acc.id,
          master_account_no: acc.consumerNo,
          master_account_name: fullName,
          approved_at: new Date().toISOString(),
        },
      },
    });
  };

  console.log("plan Data", JSON.stringify(planData));

  // Debug: Check if hook is being called and data transformation
  useEffect(() => {
    console.log("usePlans raw data:", planData);
    console.log("Transformed plans:", transformedPlans);
  }, [planData, transformedPlans]);

  // Get selected plan and calculate total dynamically with null checks
  const selectedPlan = useMemo(() => {
    // 1. Try to find the selected plan in servicePlans (from usePlans API)
    if (selectedPlanId && servicePlans && servicePlans.length > 0) {
      const matchById = servicePlans.find(
        (p) => String(p?.id) === String(selectedPlanId),
      );
      if (matchById) return matchById;

      const matchByName = servicePlans.find(
        (p) =>
          p?.name?.trim().toLowerCase() ===
          String(selectedPlanId).trim().toLowerCase(),
      );
      if (matchByName) return matchByName;
    }

    // 2. Check if selectedPlanId or unitPlanName matches unitPlanObj
    if (unitPlanObj) {
      if (
        !selectedPlanId ||
        String(unitPlanObj.id) === String(selectedPlanId) ||
        (unitPlanObj.name &&
          selectedPlanId &&
          unitPlanObj.name.trim().toLowerCase() ===
            String(selectedPlanId).trim().toLowerCase()) ||
        (unitPlanName &&
          unitPlanObj.name &&
          unitPlanObj.name.trim().toLowerCase() ===
            unitPlanName.trim().toLowerCase())
      ) {
        return unitPlanObj;
      }
    }

    // 3. Try to match by unitPlanName in servicePlans
    if (unitPlanName && servicePlans && servicePlans.length > 0) {
      const matchByUnitPlan = servicePlans.find(
        (p) =>
          p?.name?.trim().toLowerCase() === unitPlanName.trim().toLowerCase(),
      );
      if (matchByUnitPlan) return matchByUnitPlan;
    }

    // 4. Check if selectedPlanId or unitPlanName matches consumerPlanObj from consumerDetailsData
    if (consumerPlanObj) {
      if (
        !selectedPlanId ||
        String(consumerPlanObj.id) === String(selectedPlanId) ||
        (consumerPlanObj.name &&
          selectedPlanId &&
          consumerPlanObj.name.trim().toLowerCase() ===
            String(selectedPlanId).trim().toLowerCase()) ||
        (unitPlanName &&
          consumerPlanObj.name &&
          consumerPlanObj.name.trim().toLowerCase() ===
            unitPlanName.trim().toLowerCase())
      ) {
        return consumerPlanObj;
      }
    }

    // 5. Fallbacks
    if (unitPlanObj) return unitPlanObj;
    if (consumerPlanObj) return consumerPlanObj;
    if (servicePlans && servicePlans.length > 0) {
      return servicePlans[0];
    }

    return null;
  }, [
    servicePlans,
    selectedPlanId,
    unitPlanName,
    unitPlanObj,
    consumerPlanObj,
  ]);
  const totalDue = useMemo(() => {
    if (
      !registrationChargesData?.result ||
      !Array.isArray(registrationChargesData.result)
    )
      return 0;
    return registrationChargesData.result.reduce(
      (acc: number, charge: any) => acc + (Number(charge.amount) || 0),
      0,
    );
  }, [registrationChargesData]);

  const displayedCharges = useMemo(() => {
    if (
      !registrationChargesData?.result ||
      !Array.isArray(registrationChargesData.result)
    ) {
      return [];
    }

    const parameterChargeCodes = new Set<string>();
    const parameterChargeNames = new Set<string>();

    const parameters = planParametersData?.result || [];
    parameters.forEach((param: any) => {
      const regValues = param.registrationAllowedValues || [];
      regValues.forEach((rc: any) => {
        if (rc.code) {
          parameterChargeCodes.add(String(rc.code).trim().toLowerCase());
        }
        if (rc.name) {
          parameterChargeNames.add(String(rc.name).trim().toLowerCase());
        }
      });
    });

    const activeChargeCodes = new Set<string>();
    const activeChargeNames = new Set<string>();

    parameters.forEach((param: any) => {
      const paramId = param.parameterId || param.parameter_id;
      const paramCode =
        param.code || param.parameterCode || param.parameter_code;
      const paramName =
        param.name || param.parameterName || param.parameter_name;

      const selectedValue =
        parameterValues[paramId] ||
        parameterValues[paramCode] ||
        parameterValues[paramName] ||
        "";

      if (!selectedValue) return;

      const regValues =
        param.registrationAllowedValues ||
        param.registration_allowed_values ||
        [];

      regValues.forEach((rc: any) => {
        const valuesList =
          rc.values || rc.allowed_values || rc.allowedValues || [];
        const isMatched = valuesList.some(
          (v: any) =>
            String(v).trim().toLowerCase() ===
            String(selectedValue).trim().toLowerCase(),
        );

        if (isMatched) {
          if (rc.code) {
            activeChargeCodes.add(String(rc.code).trim().toLowerCase());
          }
          if (rc.name) {
            activeChargeNames.add(String(rc.name).trim().toLowerCase());
          }
        }
      });
    });

    return registrationChargesData.result.filter((charge: any) => {
      const chargeName = (
        charge.chargeName ||
        charge.charge_name ||
        charge.name ||
        ""
      )
        .trim()
        .toLowerCase();
      const chargeCode = (
        charge.chargeCode ||
        charge.charge_code ||
        charge.code ||
        ""
      )
        .trim()
        .toLowerCase();

      // Always show Security Deposit
      const isSecurityDeposit =
        chargeName.includes("security deposit") ||
        chargeCode.includes("security_deposit") ||
        chargeCode.includes("securitydeposit");

      if (isSecurityDeposit) {
        return true;
      }

      // Only show other charges if they are actively matched by the parameter selection
      return (
        activeChargeCodes.has(chargeCode) || activeChargeNames.has(chargeName)
      );
    });
  }, [registrationChargesData, planParametersData, parameterValues]);

  const displayedTotalDue = useMemo(() => {
    return displayedCharges.reduce(
      (acc: number, charge: any) => acc + (Number(charge.amount) || 0),
      0,
    );
  }, [displayedCharges]);

  const [isPlanConfirmed, setIsPlanConfirmed] = useState<boolean>(
    Boolean(initialPlanId),
  );

  // Filter plans based on search query with null checks
  const filteredPlans = servicePlans.filter(
    (plan) =>
      plan &&
      (plan.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plan.description?.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const isPlanMismatch = useMemo(() => {
    if (!selectedPlan?.name || !unitPlanName) return false;
    return (
      selectedPlan.name.trim().toLowerCase() !==
      unitPlanName.trim().toLowerCase()
    );
  }, [selectedPlan?.name, unitPlanName]);

  // Handle plan selection (updates UI selection state, no API call on select plan button)
  const handlePlanSelect = (planId: string) => {
    if (!planId) {
      console.warn("planId is empty");
      return;
    }

    const isUnitSelected = Boolean(selectedUnitCode || isPremiseButtonClicked);

    // Validate that a unit has been selected first
    if (!isUnitSelected) {
      toast({
        title: "Unit not selected",
        description: "Please select unit before selecting a plan",
        variant: "destructive",
      });
      return;
    }

    setSelectedPlanId(planId);

    const completePlanData = planData?.result?.find(
      (p: any) => p?.id?.toString() === planId,
    );
    const chosenPlanName =
      completePlanData?.planName || completePlanData?.name || "";

    if (
      chosenPlanName &&
      unitPlanName &&
      chosenPlanName.trim().toLowerCase() === unitPlanName.trim().toLowerCase()
    ) {
      setIsPlanConfirmed(true);
    } else {
      setIsPlanConfirmed(false);
    }

    const transformedPlan = servicePlans.find((p) => p?.id === planId);
    if (onPlanSelect && transformedPlan) {
      onPlanSelect(planId, transformedPlan);
    }

    // If unit has no plan attached (plan is null), attach plan to the unit on select plan
    if (!hasUnitPlan && planId) {
      executeUpdatePlanOnUnit(planId);
    }
  };

  // Execute Update Plan API call when clicking "Yes, Update Plan on Unit" or when attaching plan on first select
  const executeUpdatePlanOnUnit = (planId: string) => {
    if (!planId) return;

    setSelectingPlanId(planId);

    const completePlanData = planData?.result?.find(
      (p: any) => p?.id?.toString() === planId,
    );

    if (!completePlanData) {
      setSelectingPlanId("");
      toast({
        title: "Plan not found",
        variant: "destructive",
      });
      return;
    }

    const targetConsumerId =
      consumerId || consumerDetailsData?.result?.id || "";
    const targetUnitCode =
      selectedUnitCode ||
      consumerDetailsData?.result?.unitInfo?.basicInfo?.code ||
      "";
    const remotePlanId = Number(completePlanData.id || planId);

    if (targetUnitCode && remotePlanId) {
      patchConfigMapMutation.mutate({
        mapType: "TERRITORY_JSON",
        code: targetUnitCode,
        remote_utility_id: getRemoteUtilityId(),
        entity_type: 11,
        plan_id: remotePlanId,
      });
    }

    updateConsumerUnitPlanMutation.mutate(
      {
        consumerId: targetConsumerId,
        payload: {
          remote_plan_id: remotePlanId,
          unit_code: targetUnitCode,
        },
      },
      {
        onSuccess: () => {
          setSelectedPlanId(planId);
          setSelectingPlanId("");
          setIsPlanConfirmed(true);

          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.some(
                (key) =>
                  typeof key === "string" &&
                  (key.includes("territoryPremiseStats") ||
                    key.includes("territory-premise-stats")),
              ),
          });
          queryClient.refetchQueries({
            queryKey: ["onboarding", "territory-premise-stats"],
          });
          queryClient.refetchQueries({
            queryKey: ["cx", "consumerDetails"],
          });

          const transformedPlan = servicePlans.find((p) => p?.id === planId);

          if (onPlanSelect && transformedPlan) {
            onPlanSelect(planId, transformedPlan);
          }

          toast({
            title: `Plan "${completePlanData.planName || completePlanData.name}" attached to unit successfully`,
            variant: "default",
          });
        },
        onError: (error: any) => {
          setSelectingPlanId("");
          toast({
            title: "Failed to update plan on unit",
            description: error?.message || "Please try again",
            variant: "destructive",
          });
        },
      },
    );
  };

  // Handle tag input

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmedTag = tagInput.trim();
      if (trimmedTag && !tags.includes(trimmedTag)) {
        const updatedTags = [...tags, trimmedTag];
        setTags(updatedTags);
        setTagInput("");

        // Call the mutation API to save tags
        editConsumerMutation.mutate(
          {
            consumerId: consumerId,
            payload: {
              tags: updatedTags,
            },
          },
          {
            onSuccess: () => {
              toast({
                title: "Tag added successfully",
                variant: "default",
              });
            },
            onError: (error: any) => {
              console.error("Failed to add tag:", error);
              toast({
                title: "Failed to add tag",
                description: error?.message || "Please try again",
                variant: "destructive",
              });
              // Revert the tag addition on error
              setTags(tags);
            },
          },
        );
      }
    }
  };

  // Remove tag
  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    const previousTags = tags;
    setTags(updatedTags);

    // Call the mutation API to save tags
    editConsumerMutation.mutate(
      {
        consumerId: consumerId,
        payload: {
          tags: updatedTags,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "Tag removed successfully",
            variant: "default",
          });
        },
        onError: (error: any) => {
          console.error("Failed to remove tag:", error);
          toast({
            title: "Failed to remove tag",
            description: error?.message || "Please try again",
            variant: "destructive",
          });
          // Revert the tag removal on error
          setTags(previousTags);
        },
      },
    );
  };
  // Handle Pay Now
  const handlePayNow = () => {
    if (!selectedPlanId) {
      alert("Please select a plan first");
      return;
    }
    if (!selectedPlan) {
      alert("Selected plan data not found");
      return;
    }
    setIsPaymentDialogOpen(true);
  };

  // Handle Payment Completion
  const handlePaymentCompleted = async (data: PaymentData) => {
    if (!data) {
      console.error("Payment data is null or undefined");
      return;
    }

    setPaymentLoading(true);
    try {
      if (onPaymentComplete) {
        await onPaymentComplete(data);
      }

      console.log("Payment completed successfully:", data);

      // Invalidate queries to update UI
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerList"],
      });

      setCompletedPaymentData(data);
      setShowReceipt(true);
      setIsPaymentDialogOpen(false);
    } catch (error) {
      console.error("Payment error:", error);
      throw error;
    } finally {
      setPaymentLoading(false);
    }
  };

  const planParameters = useMemo(() => {
    return (
      planParametersData?.result?.map((param: any) => {
        const paramId = param.parameterId || param.parameter_id;
        const code = param.code || param.parameterCode || param.parameter_code;
        const name = param.name || param.parameterName || param.parameter_name;
        const val =
          parameterValues[paramId] !== undefined
            ? parameterValues[paramId]
            : parameterValues[code] !== undefined
              ? parameterValues[code]
              : parameterValues[name] !== undefined
                ? parameterValues[name]
                : "";

        return {
          parameter_name: name,
          parameter_code: code,
          value: val,
        };
      }) || []
    );
  }, [planParametersData, parameterValues]);

  // Handle Pay Later
  const handlePayLater = () => {
    if (!selectedPlanId) {
      alert("Please select a plan first");
      return;
    }
    setIsDeferredDialogOpen(true);
  };

  const handleDeferredContinue = async () => {
    setIsDeferredDialogOpen(false);

    try {
      const paymentPayload = {
        remote_utility_id: remoteUtilityId,
        consumer: Number(consumerId),
        is_plan_payment: true,
        payment_plan_mode: "add_in_first_bill",
        additional_data: {
          is_plan_payment_done: true,
          plan_parameters: planParameters,
          plan_extra_data: {
            charges: registrationChargesData?.result || [],
            total_due: totalDue,
          },
        },
      };

      await createPlanPaymentMutation.mutateAsync(paymentPayload);

      if (consumerId) {
        navigate(nextStep);
      }
      if (onPayLater && selectedPlanId) {
        onPayLater(selectedPlanId);
      }

      // Invalidate queries to update UI
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerList"],
      });
    } catch (error) {
      console.error("Add in first bill payment error:", error);
      toast({
        title: "Add in first bill Error",
        description: "Failed to process option.",
        variant: "destructive",
      });
    }
  };

  const handlePayLaterContinue = async () => {
    setIsPayLaterWarningOpen(false);

    try {
      const paymentPayload = {
        remote_utility_id: remoteUtilityId,
        consumer: Number(consumerId),
        is_plan_payment: true,
        payment_plan_mode: "pay_later",
        additional_data: {
          is_plan_payment_done: true,
          plan_parameters: planParameters,
          plan_extra_data: {
            charges: registrationChargesData?.result || [],
            total_due: totalDue,
          },
        },
      };

      await createPlanPaymentMutation.mutateAsync(paymentPayload);

      if (consumerId) {
        navigate(nextStep);
      }
      if (onPayLater && selectedPlanId) {
        onPayLater(selectedPlanId);
      }

      // Invalidate queries to update UI
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerList"],
      });
    } catch (error) {
      console.error("Pay later payment error:", error);
      toast({
        title: "Pay Later Error",
        description: "Failed to process pay later option.",
        variant: "destructive",
      });
    }
  };

  const handleContinueToInstallation = async () => {
    try {
      if (consumerId) {
        const payload = {
          additional_data: {
            plan_parameters: planParameters || [],
          },
        };
        await updateConsumerMutation.mutateAsync({
          consumerId: String(consumerId),
          payload,
        });
      }
    } catch (error) {
      console.error("Error updating consumer plan parameters:", error);
    } finally {
      navigate(nextStep);
    }
  };

  // Handle Continue from Receipt
  const handleReceiptContinue = () => {
    setShowReceipt(false);
    setCompletedPaymentData(null);
    console.log("Continuing to installation...");

    if (consumerId) {
      navigate(nextStep);
    } else {
      toast({
        title: "Navigation Error",
        description: "Consumer ID is missing",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {!showReceipt &&
        !consumerDetailsData?.result?.paymentLastData?.isPlanPayment && (
          <div
            className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-1">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Set Account and Plan
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Configure account type and select the appropriate service
                    plan
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Account Type Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type
                </label>
                <select
                  value={accountType}
                  onChange={(e) => handleAccountTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="" disabled>
                    Select account type...
                  </option>
                  {ownerTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {accountType === "1" && (
                  <div className="mt-4 p-4 border border-blue-100 bg-blue-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {accountType === "1"
                        ? "Search master account"
                        : "Search master account"}
                    </label>
                    {!selectedMasterAccount ? (
                      <div className="space-y-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={masterSearchQuery}
                            onChange={(e) =>
                              setMasterSearchQuery(e.target.value)
                            }
                            placeholder="Search by account number or name..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                          />
                        </div>

                        {masterSearchQuery.length >= 2 && (
                          <div className="bg-white border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                            {isSearchingMaster ? (
                              <div className="p-3 text-center text-sm text-gray-500">
                                Searching...
                              </div>
                            ) : masterAccounts.length > 0 ? (
                              masterAccounts.map((acc: any) => {
                                const fullName =
                                  `${acc.firstName || ""} ${acc.lastName || ""}`.trim();
                                return (
                                  <div
                                    key={acc.consumerNo || acc.id}
                                    onClick={() =>
                                      handleSelectMasterAccount(acc)
                                    }
                                    className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {fullName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      #{acc.consumerNo}{" "}
                                      {acc.subAccountCount
                                        ? `• ${acc.subAccountCount} sub-accounts`
                                        : ""}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="p-3 text-center text-sm text-gray-500">
                                No master accounts found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-md p-4 relative">
                        <button
                          onClick={() => setSelectedMasterAccount(null)}
                          className="absolute top-4 right-4 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Change
                        </button>
                        <h4 className="font-semibold text-gray-900 mb-3 border-b border-gray-100 pb-2">
                          {accountType === "1"
                            ? "Master Account Details"
                            : "Owner Details"}
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 mb-1">Account Number</p>
                            <p className="font-medium text-gray-900">
                              {selectedMasterAccount.accountNumber}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Account Name</p>
                            <p className="font-medium text-gray-900">
                              {selectedMasterAccount.accountName}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">
                              Attached Sub-accounts
                            </p>
                            <p className="font-medium text-gray-900">
                              {selectedMasterAccount.subAccountCount}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500 mb-1">
                              Service Address
                            </p>
                            <p className="font-medium text-gray-900">
                              {serviceAddress}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {accountType === "4" && (
                <div className="mt-6 space-y-4 p-4 border border-blue-100 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Manual Owner Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value)}
                        placeholder="Enter owner name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Phone Number{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={ownerPhone}
                        onChange={(e) => setOwnerPhone(e.target.value)}
                        placeholder="Enter owner phone"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={ownerEmail}
                        onChange={(e) => setOwnerEmail(e.target.value)}
                        placeholder="Enter owner email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Address
                      </label>
                      <input
                        type="text"
                        value={ownerAddress}
                        onChange={(e) => setOwnerAddress(e.target.value)}
                        placeholder="Enter owner address"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      onClick={handleSaveTenantInfo}
                      disabled={editConsumerMutation.isPending}
                    >
                      {editConsumerMutation.isPending
                        ? "Saving..."
                        : "Save Info"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Account Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Tags
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Enter a tag and press Enter or comma"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <p className="text-xs text-gray-500">
                    Press Enter or comma to add a tag
                  </p>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Billing Account Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing account
                </label>
                <select
                  value={isBillingAccount}
                  onChange={(e) => {
                    setIsBillingAccount(e.target.value);
                    if (e.target.value === "no") {
                      setSelectedPlanId("");
                      setIsNoPlanSelected(false);
                      updateConsumer({
                        consumerId: consumerId || "",
                        payload: { is_billing_account: 0 },
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-6"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* Select Plan Section */}
              {isBillingAccount === "yes" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Plan
                  </label>

                  {!selectedPlanId ? (
                    <>
                      {/* Search Box */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search plans by name..."
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>

                      {/* Plans Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-2">
                        {filteredPlans.length === 0 ? (
                          <div className="col-span-3 text-center py-8 text-gray-500">
                            {servicePlans.length === 0
                              ? "No plans available"
                              : "No plans found matching your search"}
                          </div>
                        ) : (
                          filteredPlans.map((plan) =>
                            plan && plan.id ? (
                              <div
                                key={plan.id}
                                className="border rounded-lg p-4 transition-all flex flex-col h-full border-gray-200 hover:border-gray-300 hover:shadow-sm"
                              >
                                <h3 className="font-semibold text-gray-900 mb-1">
                                  {plan.name || "Unnamed Plan"}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  {plan.description ||
                                    "No description available"}
                                </p>
                                <div className="mb-3">
                                  <span className="text-xs text-blue-600 font-medium">
                                    Billing Frequency:{" "}
                                    {plan.billingFrequency || "NA"}
                                  </span>
                                </div>
                                <div className="mb-4">
                                  <p className="text-xs font-medium text-gray-700 mb-2">
                                    Utility Services:
                                  </p>
                                  {plan.utilityServices &&
                                  plan.utilityServices.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                      {plan.utilityServices.map(
                                        (service, index) =>
                                          service ? (
                                            <div
                                              key={index}
                                              className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 border border-blue-100 text-xs font-medium text-blue-700"
                                            >
                                              {service.name || "N/A"}
                                              {service.type && (
                                                <span className="ml-1 text-blue-500 font-normal">
                                                  ({service.type})
                                                </span>
                                              )}
                                            </div>
                                          ) : null,
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">
                                      No services available
                                    </p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handlePlanSelect(plan.id)}
                                  disabled={selectingPlanId === plan.id}
                                  className="mt-auto w-full py-2 rounded-md font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {selectingPlanId === plan.id
                                    ? "Selecting..."
                                    : "Select Plan"}
                                </button>
                              </div>
                            ) : null,
                          )
                        )}
                      </div>
                    </>
                  ) : (
                    /* Selected Plan Summary */
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-blue-200">
                        <h3 className="text-base font-semibold text-gray-900">
                          Selected Plan
                        </h3>
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            onClick={() => setSelectedPlanId("")}
                            variant="outline"
                            disabled={selectingPlanId !== ""}
                          >
                            Change Plan
                          </Button>

                          <Button
                            type="button"
                            onClick={() => setIsRateDetailsOpen(true)}
                            variant="outline"
                          >
                            View Rates
                          </Button>
                        </div>
                      </div>

                      {selectedPlan ? (
                        <>
                          <div className="space-y-3 mb-6">
                            <div>
                              <h4 className="text-lg font-semibold text-blue-900">
                                {selectedPlan.name || "Unnamed Plan"}
                              </h4>
                              <p className="text-sm text-gray-700 mt-1">
                                {selectedPlan.description ||
                                  "No description available"}
                              </p>
                            </div>
                            <div>
                              <span className="text-sm text-blue-700 font-medium">
                                Billing Frequency:{" "}
                                {selectedPlan.billingFrequency || "NA"}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900 mb-2">
                                Utility Services:
                              </p>
                              {selectedPlan.utilityServices &&
                              selectedPlan.utilityServices.length > 0 ? (
                                <div className="space-y-2">
                                  {selectedPlan.utilityServices.map(
                                    (service, index) =>
                                      service ? (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between text-sm"
                                        >
                                          <span className="text-gray-900">
                                            {service.name || "N/A"}
                                          </span>
                                          <span className="text-blue-700 font-medium">
                                            {service.type || ""}
                                          </span>
                                        </div>
                                      ) : null,
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No services available
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Plan Mismatch Warning Banner */}
                          {!isPlanConfirmed && isPlanMismatch && (
                            <div className="bg-amber-50/90 border border-amber-200 rounded-lg p-4 mb-6">
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="space-y-2 flex-1">
                                  <h4 className="text-sm font-semibold text-amber-900">
                                    Plan Mismatch Detected
                                  </h4>
                                  <p className="text-sm text-amber-800">
                                    The selected plan (
                                    <strong className="font-semibold">
                                      {selectedPlan.name}
                                    </strong>
                                    ) is different from the plan currently
                                    attached to this unit (
                                    <strong className="font-semibold">
                                      {unitPlanName}
                                    </strong>
                                    ). Do you want to update the plan on the
                                    unit?
                                  </p>
                                  <div className="flex items-center gap-3 pt-2">
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        executeUpdatePlanOnUnit(selectedPlanId)
                                      }
                                      className="bg-amber-600 hover:bg-amber-700 text-white font-medium text-xs px-4 py-2"
                                      disabled={selectingPlanId !== ""}
                                    >
                                      {selectingPlanId === selectedPlanId
                                        ? "Updating..."
                                        : "Yes, Update Plan on Unit"}
                                    </Button>
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        setIsPlanConfirmed(true);
                                      }}
                                      variant="outline"
                                      className="bg-white border-amber-300 text-amber-900 hover:bg-amber-50 text-xs px-4 py-2"
                                    >
                                      Keep Current Unit Plan
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Asset Details */}
                          <div className="rounded-lg pt-4 space-y-3">
                            <div className="flex items-center gap-2 mb-3">
                              <Briefcase className="w-5 h-5 text-blue-600" />
                              <h4 className="text-sm font-semibold text-gray-900">
                                Asset Details
                              </h4>
                            </div>
                            {planParametersData?.result?.length > 0 && (
                              <div className="grid grid-cols-2 gap-4">
                                {planParametersData.result.map((param: any) => {
                                  const paramId =
                                    param.parameterId || param.parameter_id;
                                  const paramCode =
                                    param.code ||
                                    param.parameterCode ||
                                    param.parameter_code;
                                  const paramName =
                                    param.name ||
                                    param.parameterName ||
                                    param.parameter_name;

                                  const val =
                                    parameterValues[paramId] ||
                                    parameterValues[paramCode] ||
                                    parameterValues[paramName] ||
                                    "";

                                  return (
                                    <div key={paramId || paramCode}>
                                      <label className="block text-sm text-gray-700 mb-1">
                                        {paramName}
                                      </label>
                                      <select
                                        value={val}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          setParameterValues((prev) => ({
                                            ...prev,
                                            [paramId]: newValue,
                                            ...(paramCode
                                              ? { [paramCode]: newValue }
                                              : {}),
                                            ...(paramName
                                              ? { [paramName]: newValue }
                                              : {}),
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                                      >
                                        <option value="">
                                          Select {paramName.toLowerCase()}
                                        </option>
                                        {(
                                          param.allowedValues ||
                                          param.allowed_values ||
                                          []
                                        )?.map((val: string) => (
                                          <option key={val} value={val}>
                                            {val}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {!planParametersData?.result?.length ||
                            planParametersData.result.every((p: any) => {
                              const paramId = p.parameterId || p.parameter_id;
                              const options =
                                p.allowedValues || p.allowed_values || [];
                              if (options.length === 0) return true;
                              return (
                                parameterValues[paramId] &&
                                parameterValues[paramId] !== ""
                              );
                            }) ? (
                              <>
                                {/* Application Fee Summary */}
                                <div className="pt-4 border-t border-gray-200 mt-4 space-y-3">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                    Application Fee Summary
                                  </h4>
                                  <div className="space-y-2">
                                    {displayedCharges.map(
                                      (charge: any, index: number) => (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between text-sm"
                                        >
                                          <span className="text-gray-700">
                                            {charge.chargeName}
                                          </span>
                                          <span className="text-gray-900 font-medium">
                                            {currency} {charge.amount ?? 0}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                    {displayedCharges.length === 0 && (
                                      <div className="text-sm text-gray-500 italic">
                                        No registration charges found.
                                      </div>
                                    )}
                                  </div>
                                  <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-900">
                                      Total Due
                                    </span>
                                    <span className="text-lg font-bold text-gray-900">
                                      {currency} {displayedTotalDue}
                                    </span>
                                  </div>
                                </div>

                                {totalDue > 0 ? (
                                  <div className="grid grid-cols-3 gap-3 pt-4">
                                    <Button
                                      type="button"
                                      onClick={handlePayNow}
                                      variant="default"
                                    >
                                      Pay Now
                                    </Button>
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        setIsPayLaterWarningOpen(true)
                                      }
                                      variant="outline"
                                    >
                                      Pay Later
                                    </Button>
                                    <Button
                                      type="button"
                                      onClick={handlePayLater}
                                      variant="outline"
                                    >
                                      Add in first bill
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={handleContinueToInstallation}
                                    disabled={
                                      isReadOnly ||
                                      updateConsumerMutation.isPending
                                    }
                                    className="w-full mt-4"
                                  >
                                    {updateConsumerMutation.isPending
                                      ? "Processing..."
                                      : "Continue to Installation"}
                                  </Button>
                                )}
                              </>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          Selected plan data not found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full mt-4">
                  <div className="border rounded-lg p-6 flex flex-col h-full bg-blue-50 border-blue-200">
                    <div className="flex justify-between items-start mb-4 pb-3 border-b border-blue-200">
                      <h3 className="font-bold text-blue-900 text-xl">
                        No Plan Needed
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 mb-4">
                      Use this option when this request is not a billing account
                      and no service plan should be attached.
                    </p>

                    <p className="text-sm font-medium text-gray-900 mb-4">
                      Billing:{" "}
                      <span className="text-gray-500 font-normal">
                        Not applicable
                      </span>
                    </p>

                    <p className="text-sm font-bold text-gray-900 mb-3">
                      Account Handling:
                    </p>
                    <div className="space-y-3 mb-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Plan Assignment</span>
                        <span className="text-gray-500 italic">
                          Not required
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Billing Setup</span>
                        <span className="text-gray-500 italic">
                          Skip billing account creation
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Fee Collection</span>
                        <span className="text-gray-500 italic">
                          No payment required
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleContinueToInstallation}
                    variant="default"
                    disabled={updateConsumerMutation.isPending}
                    className="w-full mt-4"
                  >
                    {updateConsumerMutation.isPending
                      ? "Processing..."
                      : "Continue to Installation"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Payment Dialog */}
      <PaymentDialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
        onPaymentComplete={handlePaymentCompleted}
        totalDue={totalDue}
        defaultAmount={totalDue}
        loading={paymentLoading}
        consumerId={Number(consumerId)}
        consumerNo={""}
        planParameters={planParameters}
      />

      {/* Payment Deferred Dialog */}
      <PaymentDeferredDialog
        open={isDeferredDialogOpen}
        onClose={() => setIsDeferredDialogOpen(false)}
        onContinue={handleDeferredContinue}
      />

      {/* Pay Later Warning Dialog */}
      <PaymentDeferredDialog
        open={isPayLaterWarningOpen}
        onClose={() => setIsPayLaterWarningOpen(false)}
        onContinue={handlePayLaterContinue}
        title="Pay Later Selected"
        noticeMessage="⚠ You have selected “Pay Later” for this NSC request."
        detailMessage="Fees must be collected separately in the Payments module. Do you want to continue to the installation step?"
      />
      <RateDetailsDialog
        open={isRateDetailsOpen}
        onClose={() => setIsRateDetailsOpen(false)}
        selectedPlan={
          planDetailsData?.result
            ? Array.isArray(planDetailsData.result)
              ? planDetailsData.result[0]
              : planDetailsData.result
            : null
        }
      />
      {/* {selectedPlan && (
        <SelectedPlanCard
          planName={selectedPlan.name}
          description={selectedPlan.description}
          billingFrequency={selectedPlan.billingFrequency}
          utilityServices={selectedPlan.utilityServices}
          processingFees={selectedPlan.processingFees}
          connectionFee={selectedPlan.connectionFee}
          securityDeposit={selectedPlan.securityDeposit}
          showFeeSummary={true}
          className="mt-4"
        />
      )} */}

      {/* Payment Receipt */}
      {/* {showReceipt && completedPaymentData && selectedPlan && (
        <PaymentReceipt
          receiptId={`RC-${Date.now()}`}
          paymentMethod={
            completedPaymentData.paymentModeDisplay ||
            consumerDetailsData?.result?.payment?.paymentModeDisplay ||
            "NA"
          }
          amountPaid={completedPaymentData.amount ?? 0}
          cashReceived={completedPaymentData.cashReceived ?? undefined}
          change={
            completedPaymentData.cashReceived
              ? completedPaymentData.cashReceived -
                (completedPaymentData.amount ?? 0)
              : undefined
          }
          feeBreakdown={[
            {
              label: "Application Processing Fee",
              amount: selectedPlan.processingFees ?? 0,
            },
            {
              label: "Service Connection Fee",
              amount: selectedPlan.connectionFee ?? 0,
            },
            {
              label: "Security Deposit",
              amount: selectedPlan.securityDeposit ?? 0,
            },
          ]}
          totalPaid={totalDue}
          onContinue={handleReceiptContinue}
          consumerDetailsData={consumerDetailsData}
        />
      )} */}
    </>
  );
};

export default SetAccountAndPlan;
